/**
 * SearchTypeahead — a Google-style hybrid search box.
 *
 * The text input is the source of truth and drives the page's live list
 * filter exactly as a plain search box would (so substring / description
 * search keeps working — just type and the table filters, or hit Enter).
 * On top of that, an *optional* autocomplete panel floats under the box:
 * as the user types, matching entities appear grouped by type. What a
 * selection does is the page's call (via `onSelectSuggestion`) — e.g. scope
 * the list to a customer, or open a job/estimate's detail page.
 *
 * Interaction:
 *   • type            → list filters live (parent owns `value`)
 *   • ↓ / ↑           → move highlight across the panel rows
 *   • Enter on row 0  → "Search …" (just closes the panel; text already filters)
 *   • Enter on a hit  → onSelectSuggestion(hit)  (parent decides what to do)
 *   • Esc / blur      → panel closes, typed text stays as the filter
 *
 * Shell rules: this is a pattern component, so it must not touch browser
 * globals. Closing is handled purely through React events — `onMouseDown`
 * preventDefault on the panel keeps focus on the input while a row is
 * clicked, and the input's `onBlur` closes on a genuine outside click.
 */

import { useEffect, useId, useMemo, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import type { SearchSuggestion } from "@shared-core/domain/searchSuggestions";
import { cn } from "../primitives/utils";

export interface SearchTypeaheadProps {
  value: string;
  onValueChange: (next: string) => void;
  /** Loads autocomplete suggestions for the typed query. Memoise this in
   *  the parent (useCallback) so the debounced fetch effect is stable. */
  fetchSuggestions: (query: string) => Promise<SearchSuggestion[]>;
  /** Called when the user picks a suggestion — the parent decides what to
   *  do (scope the list to a customer, open an entity's detail, …). */
  onSelectSuggestion: (suggestion: SearchSuggestion) => void;
  placeholder?: string;
  ariaLabel?: string;
  /** Width / layout override for the outer container. */
  className?: string;
}

const FETCH_DEBOUNCE_MS = 200;

function isBlank(query: string): boolean {
  return query.trim().length === 0;
}

export function SearchTypeahead({
  value,
  onValueChange,
  fetchSuggestions,
  onSelectSuggestion,
  placeholder = "Search…",
  ariaLabel,
  className,
}: SearchTypeaheadProps) {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  // Row 0 is always the "Search …" submit row; suggestions follow at 1..n.
  const [highlight, setHighlight] = useState(0);
  const listboxId = useId();

  const hasQuery = !isBlank(value);
  const debounced = useDebouncedValue(value, FETCH_DEBOUNCE_MS);

  useEffect(() => {
    if (!open || isBlank(debounced)) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchSuggestions(debounced)
      .then((next) => {
        if (!cancelled) {
          setSuggestions(next);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSuggestions([]);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [debounced, open, fetchSuggestions]);

  // Keep the highlight on the submit row whenever the result set shifts.
  useEffect(() => {
    setHighlight(0);
  }, [suggestions, value]);

  // Group suggestions for rendering while preserving source order, and keep
  // a flat list so keyboard indexing (row N) maps to one suggestion.
  const groups = useMemo(() => {
    const order: string[] = [];
    const byGroup = new Map<string, SearchSuggestion[]>();
    for (const s of suggestions) {
      if (!byGroup.has(s.group)) {
        byGroup.set(s.group, []);
        order.push(s.group);
      }
      byGroup.get(s.group)!.push(s);
    }
    return order.map((name) => ({ name, items: byGroup.get(name)! }));
  }, [suggestions]);

  const panelOpen = open && hasQuery;
  // Total selectable rows: the submit row (0) + each suggestion.
  const rowCount = suggestions.length + 1;

  function move(delta: number) {
    setHighlight((h) => {
      const next = h + delta;
      if (next < 0) return 0;
      if (next > rowCount - 1) return rowCount - 1;
      return next;
    });
  }

  function activate(index: number) {
    if (index === 0) {
      // "Search …" — text already drives the filter; just dismiss.
      setOpen(false);
      return;
    }
    const suggestion = suggestions[index - 1];
    if (suggestion) {
      setOpen(false);
      onSelectSuggestion(suggestion);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      if (!panelOpen) {
        setOpen(true);
        return;
      }
      e.preventDefault();
      move(1);
    } else if (e.key === "ArrowUp") {
      if (!panelOpen) return;
      e.preventDefault();
      move(-1);
    } else if (e.key === "Enter") {
      if (panelOpen) {
        e.preventDefault();
        activate(highlight);
      }
    } else if (e.key === "Escape") {
      if (panelOpen) {
        e.preventDefault();
        setOpen(false);
      }
    }
  }

  return (
    <div className={cn("relative", className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          role="combobox"
          aria-expanded={panelOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-label={ariaLabel}
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            onValueChange(e.target.value);
            setOpen(!isBlank(e.target.value));
          }}
          onFocus={() => {
            if (hasQuery) setOpen(true);
          }}
          onBlur={() => setOpen(false)}
          onKeyDown={handleKeyDown}
          className="w-full pl-10 pr-4 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {panelOpen && (
        <div
          // Keep focus on the input while a row is clicked so the input's
          // onBlur (our outside-click close) does not fire first.
          onMouseDown={(e) => e.preventDefault()}
          className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-md"
        >
          <ul
            id={listboxId}
            role="listbox"
            className="max-h-80 overflow-y-auto py-1"
          >
            {/* Row 0 — plain substring search. */}
            <li
              role="option"
              aria-selected={highlight === 0}
              onMouseEnter={() => setHighlight(0)}
              onClick={() => activate(0)}
              className={cn(
                "flex items-center justify-between gap-2 px-3 py-2 text-sm cursor-pointer",
                highlight === 0 ? "bg-accent text-accent-foreground" : "",
              )}
            >
              <span className="truncate">
                Search “<span className="font-medium">{value}</span>”
              </span>
              <span className="shrink-0 text-xs text-muted-foreground">
                ↵ Enter
              </span>
            </li>

            {loading && (
              <li className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
                <Loader2 className="size-3 animate-spin" /> Finding matches…
              </li>
            )}

            {!loading && suggestions.length === 0 && (
              <li className="px-3 py-2 text-xs text-muted-foreground">
                No matches — press Enter to search anyway.
              </li>
            )}

            {!loading &&
              groups.map((group) => (
                <li key={group.name} role="presentation">
                  <div className="px-3 pt-2 pb-1 text-xs font-medium text-muted-foreground">
                    {group.name}
                  </div>
                  <ul role="presentation">
                    {group.items.map((s) => {
                      // Recover this suggestion's flat row index for highlight sync.
                      const flatIndex = suggestions.indexOf(s) + 1;
                      const active = highlight === flatIndex;
                      return (
                        <li
                          key={`${s.group}:${s.id}`}
                          role="option"
                          aria-selected={active}
                          onMouseEnter={() => setHighlight(flatIndex)}
                          onClick={() => activate(flatIndex)}
                          className={cn(
                            "px-3 py-2 text-sm cursor-pointer",
                            active ? "bg-accent text-accent-foreground" : "",
                          )}
                        >
                          <div className="truncate">{s.label}</div>
                          {s.hint && (
                            <div className="truncate text-xs text-muted-foreground">
                              {s.hint}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
}
